import React from 'react';
import {useTranslation} from 'react-i18next';
import {useTrascina} from './FasciaFornitori';

/**
 * LA RIGA CHE SCORRE CON LE FRECCE: le categorie (o le schede) in fila su
 * UNA riga, fra una freccia all'inizio e una alla fine. Le frecce sono
 * d'oro e si illuminano (respirano piano, e al passaggio del mouse si
 * accendono di piu'); toccandole la riga scorre di quasi una larghezza. La
 * freccia dalla parte in cui non c'e' piu' niente si spegne; se tutto ci
 * sta, le frecce non si vedono. Col dito e col trackpad la riga scorre da
 * se', col mouse si trascina (useTrascina, come la fascia dei fornitori).
 *
 * La usano la lobby di Casino', Live casino e Crash game (fra la ricerca e
 * il filtro dei fornitori) e il blocco «Cerca e categorie» della Home.
 *
 * `children`: le pastiglie. `attiva`: la chiave di quella scelta: quando
 * cambia, la riga la porta in vista. Sul telefono le frecce non ci sono
 * (Casino.css): si scorre col dito.
 */
export default function RigaScorrevole({children, attiva, etichetta, className}) {
  const {t} = useTranslation();
  const fila = React.useRef(null);
  const trascina = useTrascina(fila);
  const [bordi, setBordi] = React.useState({prima: false, dopo: false});

  const misura = React.useCallback(() => {
    const el = fila.current;
    if (!el) return;
    const prima = el.scrollLeft > 2;
    const dopo = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setBordi((b) => (b.prima === prima && b.dopo === dopo ? b : {prima, dopo}));
  }, []);

  React.useLayoutEffect(() => {
    const el = fila.current;
    if (!el) return undefined;
    misura();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', misura);
      return () => window.removeEventListener('resize', misura);
    }
    const oss = new ResizeObserver(misura);
    oss.observe(el);
    Array.from(el.children).forEach((c) => oss.observe(c));
    return () => oss.disconnect();
  }, [misura, children]);

  /* La pastiglia scelta in vista, senza muovere la pagina in su o in giu'. */
  React.useEffect(() => {
    const el = fila.current;
    const scelta = el && el.querySelector('[aria-pressed="true"], [aria-selected="true"]');
    if (!scelta) return;
    /* La fila e' position: relative (Casino.css): offsetLeft si misura da lei. */
    const sinistra = scelta.offsetLeft;
    if (sinistra < el.scrollLeft || sinistra + scelta.offsetWidth > el.scrollLeft + el.clientWidth) {
      scorri(el, sinistra - (el.clientWidth - scelta.offsetWidth) / 2);
    }
  }, [attiva]);

  const passo = (verso) => {
    const el = fila.current;
    if (el) scorri(el, el.scrollLeft + verso * Math.max(120, el.clientWidth * 0.8));
  };

  return (
    <div className={'cas-scorrevole' + (bordi.prima || bordi.dopo ? ' esce' : '') + (className ? ' ' + className : '')}>
      <button type={'button'} className={'cas-freccia prima'} onClick={() => passo(-1)} disabled={!bordi.prima}
        aria-label={t('Indietro')} tabIndex={bordi.prima ? undefined : -1}>
        <svg width={'18'} height={'18'} viewBox={'0 0 24 24'} fill={'none'} stroke={'currentColor'} strokeWidth={'2.6'}
          strokeLinecap={'round'} strokeLinejoin={'round'} aria-hidden={'true'}><polyline points={'15 5 8 12 15 19'}/></svg>
      </button>
      <div className={'cas-scorrevole-fila'} ref={fila} onScroll={misura} role={'group'} aria-label={etichetta} {...trascina}>
        {children}
      </div>
      <button type={'button'} className={'cas-freccia dopo'} onClick={() => passo(1)} disabled={!bordi.dopo}
        aria-label={t('Avanti')} tabIndex={bordi.dopo ? undefined : -1}>
        <svg width={'18'} height={'18'} viewBox={'0 0 24 24'} fill={'none'} stroke={'currentColor'} strokeWidth={'2.6'}
          strokeLinecap={'round'} strokeLinejoin={'round'} aria-hidden={'true'}><polyline points={'9 5 16 12 9 19'}/></svg>
      </button>
    </div>
  );
}

function scorri(el, x) {
  const dove = Math.max(0, Math.min(x, el.scrollWidth - el.clientWidth));
  if (typeof el.scrollTo === 'function') el.scrollTo({left: dove, behavior: 'smooth'});
  else el.scrollLeft = dove;
}
